import { useEffect, useRef, useState } from "react";
import { AUTH_API_HOST } from "@/api/apis";
import { User } from "@/models/User";
import { startSessionWatch } from "@/hooks/auth-session-watch";

/**
 * auth 로그인 화면을 결제 모달 **안에 iframe 으로** 띄우고 완료를 감지한다.
 *
 * 시안(29435:37715)은 `Checkout` 을 누르면 이메일 입력·로그인 버튼·SSO 3종이 모달 안에
 * **한 번에** 보인다. 그 구조를 만들면서도 자격증명 폼을 홈페이지에 복제하지 않는 방법이
 * 임베드다 — 화면의 문서 오리진이 그대로 auth 다(auth#452 가 로그인 플로우 경로에 한해
 * `frame-ancestors` 를 열어줬다).
 *
 * 부수 효과로 **세션 리코더 격리**가 유지된다. bhaptics.com 에는 Hotjar·Clarity 가 있는데
 * 크로스 오리진 iframe 내부는 읽지 못한다 — 폼을 홈페이지에 두면 잃었을 성질이다.
 *
 * ## 감지는 팝업과 같은 코드를 쓴다 (`auth-session-watch`)
 * 빠른 경로(iframe 의 postMessage) + 보장 경로(`/api/user` 폴링).
 *
 * **폴링은 모달이 열려 있는 내내 돈다** — 클릭 이후가 아니다. SSO 는 iframe 밖 팝업으로
 * breakout 되는데(프로바이더가 프레이밍을 거부한다) 그 팝업의 완료 신호는 auth iframe 을
 * opener 로 보고 홈페이지 오리진으로 쏘기 때문에 **브라우저가 버린다.** 그 경로에서는
 * 폴링이 유일한 감지 수단이다.
 */

/** 부모 → iframe. 부모 오리진을 알리는 핸드셰이크(iframe 은 부모 오리진을 알 수 없다). */
const INIT = "bhaptics:embed:init";
/** iframe → 부모. 문서 높이. */
const RESIZE = "bhaptics:embed:resize";
/** iframe → 부모. 로그인 완료(빠른 경로). */
const SUCCESS = "bhaptics:auth:success";
/**
 * iframe → 부모. **SSO 창을 대신 열어달라는 요청** (auth#453).
 *
 * 프로바이더는 프레이밍을 거부하므로 iframe 안에서 진행할 수 없다. iframe 이 직접
 * `window.open` 하면 `/popup-complete` 가 opener(= iframe, auth 오리진)에 홈페이지
 * 오리진을 targetOrigin 으로 보내 **신호가 버려진다.** 부모가 열면 opener 가 홈페이지라
 * 빠른 경로가 살아난다.
 */
const SSO_REQUEST = "bhaptics:embed:sso";

/** auth 가 실제로 지원하는 provider id. 그 외 값은 무시한다(iframe 입력을 그대로 믿지 않는다). */
const ALLOWED_PROVIDERS = ["google", "microsoft-entra-id", "facebook"];

/**
 * init 재시도. **한 번만 보내면 경쟁에서 진다** — 부모의 `load` 핸들러가 iframe 의
 * `useEffect`(리스너 부착)보다 먼저 돌 수 있고, 그러면 핸드셰이크가 유실돼 높이가
 * 영영 안 온다(모달이 초기 높이에 고정된다). 첫 `resize` 를 받을 때까지 재시도한다.
 */
const INIT_RETRY_MS = 300;
const INIT_RETRY_LIMIT = 20; // 6초

/**
 * 초기 높이. 실측(416px 폭, 코드 로그인 + SSO 3분할)이 253px 이라 여유를 조금 둔다.
 * 브리지가 곧 정확한 값으로 덮으므로 **레이아웃 점프를 줄이는 용도**일 뿐이다.
 */
const INITIAL_HEIGHT = 260;

export function useAuthEmbed({
  onAuthenticated,
  lang,
  onSsoRequested,
}: {
  /** 검증된 세션이 확인되면 호출. 호출처가 캐시 갱신과 결제 진행을 담당한다. */
  onAuthenticated: (user: User) => void;
  /** auth 화면 언어. 홈페이지의 현재 로케일(`router.locale`)을 그대로 넘긴다. */
  lang?: string;
  /**
   * iframe 이 SSO 창을 요청했을 때. 호출처가 `useAuthPopup` 으로 연다 —
   * **부모가 열어야** 완료의 빠른 경로가 산다(위 `SSO_REQUEST` 주석).
   */
  onSsoRequested?: (provider: string) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  /**
   * 재시도를 다 쓰도록 `resize` 가 한 번도 안 왔다 = **우리 페이지가 프레임 안에서 살아
   * 있지 않다.** 프레이밍 거부(`frame-ancestors` 오설정·auth 장애·확장 프로그램 차단)가
   * 대표적이다.
   *
   * ⚠️ 이 신호가 없으면 사용자는 **빈 박스와 게스트 버튼만** 보고 로그인할 방법이 없다 —
   * 팝업 시절의 "차단되면 리다이렉트" 탈출로가 사라진 자리다. 프레이밍 실패는 `onError`
   * 로 안 잡히므로(오류 문서도 load 로 친다) 이렇게 간접 판정한다.
   */
  const [isStalled, setIsStalled] = useState(false);

  /**
   * src 를 **한 번만 계산해 고정**한다. 렌더마다 새로 만들면 높이 갱신으로 인한 리렌더가
   * iframe 을 리로드해서 **입력 중인 이메일·OTP 코드가 날아간다.** 값이 같으면 React 가
   * 재로드하지 않지만, 그 안전성을 `window.location.href` 의 안정성에 의존하지 않는다.
   */
  const [src] = useState(() => {
    if (typeof window === "undefined") return "";
    const url = new URL(`${AUTH_API_HOST}/login`);
    url.searchParams.set("mode", "embed");
    url.searchParams.set("success-url", window.location.href);
    if (lang) url.searchParams.set("lang", lang);
    return url.toString();
  });

  // 최신 콜백을 ref 로 들고 있어 effect 의존성에서 뺀다 — 콜백이 매 렌더 새로 만들어지는
  // 인라인 함수라, 의존성에 넣으면 **감시가 매 렌더 재시작**되고 폴링이 처음부터 다시 돈다.
  const onAuthenticatedRef = useRef(onAuthenticated);
  const onSsoRequestedRef = useRef(onSsoRequested);
  // 렌더 중에 ref 를 쓰면 안 된다(`react-hooks/refs`) — 커밋 후 갱신한다. 초기값은
  // `useRef` 인자로 이미 들어가 있어 첫 감지에도 최신 콜백이 쓰인다.
  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
    onSsoRequestedRef.current = onSsoRequested;
  });

  useEffect(() => {
    let ready = false;
    let retries = 0;

    const watch = startSessionWatch({
      onAuthenticated: (user) => onAuthenticatedRef.current(user),
    });

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== AUTH_API_HOST) return;
      if (e.data?.type === RESIZE) {
        ready = true;
        // 늦게라도 살아나면 탈출로를 거둔다(느린 회선에서 6초를 넘길 수 있다).
        setIsStalled(false);
        const next = Number(e.data.height);
        // 비정상값(0·음수·NaN·과대)으로 모달을 무너뜨리지 않는다.
        if (Number.isFinite(next) && next > 0 && next < 5000) setHeight(next);
        return;
      }
      if (e.data?.type === SSO_REQUEST) {
        const provider = String(e.data.provider ?? "");
        // iframe 은 우리 오리진이지만 값은 그대로 믿지 않는다 — 고정 맵으로 거른다.
        if (ALLOWED_PROVIDERS.includes(provider)) {
          onSsoRequestedRef.current?.(provider);
        }
        return;
      }
      if (e.data?.type === SUCCESS) watch.check();
    };
    window.addEventListener("message", onMessage);

    const sendInit = () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: INIT },
        AUTH_API_HOST,
      );
    };
    sendInit();
    const retryTimer = setInterval(() => {
      retries += 1;
      if (ready) {
        clearInterval(retryTimer);
        return;
      }
      if (retries > INIT_RETRY_LIMIT) {
        clearInterval(retryTimer);
        // 재시도를 다 썼는데 응답이 없다 → 탈출로를 연다.
        setIsStalled(true);
        return;
      }
      sendInit();
    }, INIT_RETRY_MS);

    return () => {
      window.removeEventListener("message", onMessage);
      clearInterval(retryTimer);
      // 실전의 취소 경로는 **언마운트**다 — 사용자가 모달을 닫으면 Radix 가 content 를
      // 언마운트한다. 여기서 안 끊으면 닫은 뒤에도 폴링이 5분간 돌고, 뒤늦게 로그인이
      // 감지되면 **누르지도 않은 결제가 진행**된다.
      watch.stop();
    };
  }, []);

  return { iframeRef, src, height, isStalled };
}
